import { authorizeGetRequest } from "api";

// Period types for financial data
export type FinancialPeriod = "daily" | "weekly" | "monthly" | "quarterly" | "yearly";

// Main dashboard response interface
export interface IFinancialDashboardResponse {
    period: {
        type?: string;
        start_date: string;
        end_date: string;
        days?: number;
    };
    revenue: {
        total_revenue: number;
        platform_cut: number;
        tax_collected: number;
        driver_earnings: number;
        penalty_revenue: number;
        average_trip_revenue: number;
        revenue_by_payment_method?: {
            cash: number;
            wallet: number;
        };
        trip_count: number;
    };
    costs: {
        total_costs: number;
        system_costs: number;
        penalty_costs: number;
        cancellation_costs: number;
        cost_breakdown?: {
            gcp_compute: number;
            sms_communications: number;
            driver_penalties: number;
            trip_cancellations: number;
            other: number;
        };
    };
    trips: {
        total_trips: number;
        completed_trips: number;
        cancelled_trips: number;
        completion_rate: number;
        average_trip_duration?: number;
        peak_hours?: string;
        busiest_day?: string;
        economics: {
            average_revenue_per_trip: number;
            cost_per_trip: number;
            profit_per_trip: number;
            profit_margin_per_trip: number;
        };
    };
    drivers: {
        total_drivers?: number;
        active_drivers: number;
        average_rating?: number;
        top_performers?: number;
        penalized_drivers?: number;
        total_earnings: number;
        wallet_balances: number;
        utilization_rate: number;
    };
    profitability: {
        gross_profit?: number;
        net_profit: number;
        gross_margin?: number;
        net_margin: number;
        profit_per_trip?: number;
        roi?: number;
        roi_percentage?: number;
        break_even_point: number;
    };
    trends: {
        revenue_trend: {
            direction: "up" | "down" | "stable";
            change_percent: number;
            period?: string;
        };
        cost_trend?: {
            direction: "up" | "down" | "stable";
            change_percent: number;
            period?: string;
        };
        trip_trend?: {
            direction: "up" | "down" | "stable";
            change_percent: number;
            period?: string;
        };
        insights?: string[];
    };
    health_score?: number;
    generated_at?: string;
}

// Business Intelligence response
export interface IBusinessIntelligenceResponse {
    kpis: Array<{
        name: string;
        value: number;
        target: number;
        status: "good" | "warning" | "critical";
        trend: "up" | "down" | "stable";
        description: string;
    }>;
    insights: string[];
    recommendations: string[];
}

// Cost Analysis response
export interface ICostAnalysisResponse {
    total_costs: number;
    cost_breakdown: Array<{
        category: string;
        amount: number;
        percentage: number;
        trend: "up" | "down" | "stable";
        optimization_potential: number;
        recommendations: string[];
    }>;
    optimization_opportunities: string[];
    projected_savings: number;
}

// Financial Health response
export interface IFinancialHealthResponse {
    score: number;
    status: "excellent" | "good" | "fair" | "poor";
    factors: Array<{
        name: string;
        score: number;
        impact: string;
    }>;
    recommendations: string[];
}

// Revenue Trends response
export interface IRevenueTrendsResponse {
    daily: Array<{
        date: string;
        revenue: number;
        trip_count: number;
    }>;
    weekly: Array<{
        week_start: string;
        revenue: number;
        average_daily_revenue: number;
    }>;
    best_day: {
        date: string;
        revenue: number;
    };
    worst_day: {
        date: string;
        revenue: number;
    };
}

// Forecasting response
export interface IForecastingResponse {
    forecast_period: string;
    predictions: Array<{
        date: string;
        predicted_revenue: number;
        confidence_level: number;
        factors: {
            seasonal_trend: string;
            day_of_week: string;
            weather_impact: string;
        };
    }>;
    accuracy_metrics: {
        mean_absolute_percentage_error: number;
        r_squared: number;
        last_trained: string;
    };
    assumptions: string[];
}

// API Functions

/**
 * Get complete financial dashboard data
 */
export const getFinancialDashboardApi = (period: FinancialPeriod = "daily", useCache: boolean = false) => {
    return authorizeGetRequest<IFinancialDashboardResponse>(
        `financial/dashboard?period=${period}`,
        useCache
    );
};

/**
 * Get business intelligence insights and KPIs
 */
export const getBusinessIntelligenceApi = (period: FinancialPeriod = "monthly", useCache: boolean = false) => {
    return authorizeGetRequest<IBusinessIntelligenceResponse>(
        `financial/business-intelligence?period=${period}`,
        useCache
    );
};

/**
 * Get cost analysis and optimization recommendations
 */
export const getCostAnalysisApi = (period: FinancialPeriod = "monthly", useCache: boolean = false) => {
    return authorizeGetRequest<ICostAnalysisResponse>(
        `financial/cost-analysis?period=${period}`,
        useCache
    );
};

/**
 * Get financial health score
 */
export const getFinancialHealthApi = (useCache: boolean = false) => {
    return authorizeGetRequest<IFinancialHealthResponse>(
        `financial/health`,
        useCache
    );
};

/**
 * Get revenue trends analysis
 */
export const getRevenueTrendsApi = (days: number = 30, useCache: boolean = false) => {
    return authorizeGetRequest<IRevenueTrendsResponse>(
        `financial/revenue/trends?days=${days}`,
        useCache
    );
};
/**
 * Get revenue forecasting
 */
export const getForecastingApi = (days: number = 30, useCache: boolean = false) => {
    return authorizeGetRequest<IForecastingResponse>(
        `financial/forecasting?forecast_days=${days}`,
        useCache
    );
};

/**
 * Get detailed revenue metrics
 */
export const getFinancialRevenueMetricsApi = (period: FinancialPeriod = "monthly") => {
    return authorizeGetRequest<any>(
        `financial/metrics/revenue?period=${period}`
    );
};

/**
 * Get detailed profitability metrics
 */
export const getFinancialProfitabilityMetricsApi = (period: FinancialPeriod = "monthly") => {
    return authorizeGetRequest<any>(
        `financial/metrics/profitability?period=${period}`
    );
};

/**
 * Get detailed trip performance metrics
 */
export const getFinancialTripMetricsApi = (period: FinancialPeriod = "monthly") => {
    return authorizeGetRequest<any>(
        `financial/metrics/trips?period=${period}`
    );
};

/**
 * Get cost monitoring and optimization analysis
 */
export const getFinancialCostMonitoringApi = () => {
    return authorizeGetRequest<any>(
        `financial/cost-monitoring`
    );
};
